"use client";

import { useEffect, useState } from "react";

import { resolveActiveSection } from "@/hooks/resolveActiveSection";

/** Reading band: from under the sticky header + jump nav down to 40% of the viewport. */
const READING_BAND_TOP_PX = 150;
const READING_BAND_BOTTOM_PERCENT = 40;

/**
 * Tracks which of the given section ids is currently being read: the section
 * covering most of the reading band just below the sticky navigation; when none
 * overlaps it, the last section already passed, or the
 * first one while the page sits above them all. Resolved from scroll position on
 * mount and on every scroll or resize frame, so a jump (to the top, to an anchor)
 * never leaves it stale. Renders the first id on the server.
 */
export function useActiveSection(ids: string[]): string | null {
  const idsKey = ids.join("|");
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    const sectionIds = idsKey ? idsKey.split("|") : [];
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);
    if (elements.length === 0) return;

    let frame = 0;
    const resolve = () => {
      frame = 0;
      setActiveId(
        resolveActiveSection(
          elements.map((element) => {
            const { top, bottom } = element.getBoundingClientRect();
            return { id: element.id, top, bottom };
          }),
          {
            top: READING_BAND_TOP_PX,
            bottom: (window.innerHeight * READING_BAND_BOTTOM_PERCENT) / 100,
          },
        ),
      );
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(resolve);
    };

    resolve();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [idsKey]);

  return activeId;
}
